import {
  DEFAULT_REPOSITORY_INDEXING_MODE,
  REPOSITORY_INDEXING_MODE_VALUES,
  type RepositoryIndexingMode,
} from "@arti/shared-source-control/indexing/runMode";
import { z } from "zod";

export type RepoIndexingMode = RepositoryIndexingMode;

export interface RepoIndexingRunContext {
  indexing_mode: RepoIndexingMode;
  run_id: string;
  run_item_id: string;
  requested_by: string | null;
  scope: string | null;
}

export interface RepoIndexingRepositoryContext {
  slug: string;
  id: string | null;
  default_branch: string | null;
  branches: string[];
  languages: string[];
  topics: string[];
  description: string | null;
  last_commit_sha: string | null;
}

export interface RepoIndexingIndexingContext {
  last_successful_run_id: string | null;
  last_summary: string | null;
  guidelines: string | null;
}

export interface RepoIndexingWorkspaceState {
  repo_path: string | null;
}

export interface RepoIndexingContext {
  run: RepoIndexingRunContext;
  repository: RepoIndexingRepositoryContext;
  indexing: RepoIndexingIndexingContext;
  workspace_state: RepoIndexingWorkspaceState;
}

const repoIndexingRunContextSchema = z.object({
  indexing_mode: z
    .enum(REPOSITORY_INDEXING_MODE_VALUES)
    .default(DEFAULT_REPOSITORY_INDEXING_MODE),
  requested_by: z.string().nullable(),
  run_id: z.string(),
  run_item_id: z.string(),
  scope: z.string().nullable(),
});

const repoIndexingRepositoryContextSchema = z.object({
  branches: z.array(z.string()),
  default_branch: z.string().nullable(),
  description: z.string().nullable(),
  id: z.string().nullable(),
  languages: z.array(z.string()),
  last_commit_sha: z.string().nullable(),
  slug: z.string(),
  topics: z.array(z.string()),
});

const repoIndexingIndexingContextSchema = z.object({
  guidelines: z.string().nullable(),
  last_successful_run_id: z.string().nullable(),
  last_summary: z.string().nullable(),
});

const repoIndexingWorkspaceStateSchema = z.object({
  repo_path: z.string().nullable(),
});

export const repoIndexingContextSchema = z.object({
  indexing: repoIndexingIndexingContextSchema,
  repository: repoIndexingRepositoryContextSchema,
  run: repoIndexingRunContextSchema,
  workspace_state: repoIndexingWorkspaceStateSchema,
});

export function parseRepoIndexingContext(
  value: unknown,
): RepoIndexingContext | null {
  const parsed = repoIndexingContextSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function buildRepoIndexingContextPrompt(
  context: RepoIndexingContext,
): string {
  return ["## Repo Indexing Context", "JSON:", JSON.stringify(context, null, 2)]
    .join("\n")
    .trim();
}
